import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Bot, Loader2, Moon, Sun } from 'lucide-react';
import AgentChat from './AgentChat';
import Login from './Login';
import { useAuth } from '../services/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import type { ListAgentSessionsOptions } from '../services/agentService';
import { AgentScheduleService, type Schedule } from '../services/agentScheduleService';

const AgentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading, token } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [recentSchedules, setRecentSchedules] = useState<Schedule[]>([]);

  const preferredSessionId = searchParams.get('session') || searchParams.get('run');
  const scheduleId = searchParams.get('schedule');
  const triggerType = searchParams.get('trigger') || undefined;
  const dateFrom = searchParams.get('dateFrom') || undefined;
  const dateTo = searchParams.get('dateTo') || undefined;

  const sessionListOptions = useMemo<ListAgentSessionsOptions | undefined>(() => {
    if (!scheduleId && !triggerType && !dateFrom && !dateTo) {
      return undefined;
    }
    return {
      scheduleId: scheduleId || undefined,
      triggerType,
      dateFrom,
      dateTo,
      limit: 200,
    };
  }, [dateFrom, dateTo, scheduleId, triggerType]);

  const sessionListLabel = useMemo(() => {
    const parts = [];
    const selectedSchedule = recentSchedules.find((schedule) => schedule.id === scheduleId);
    if (scheduleId) parts.push(`schedule ${selectedSchedule?.name || scheduleId}`);
    if (triggerType) parts.push(`trigger ${triggerType}`);
    if (dateFrom) parts.push(`from ${dateFrom}`);
    if (dateTo) parts.push(`to ${dateTo}`);
    return parts.length > 0 ? `Filtered to ${parts.join(' · ')}` : null;
  }, [dateFrom, dateTo, recentSchedules, scheduleId, triggerType]);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    let cancelled = false;

    AgentScheduleService.list(token)
      .then((schedules) => {
        if (cancelled) return;
        const ordered = [...schedules]
          .sort((left, right) => {
            const leftPriority = left.currentRunAgentSessionId ? 1 : 0;
            const rightPriority = right.currentRunAgentSessionId ? 1 : 0;
            if (leftPriority !== rightPriority) return rightPriority - leftPriority;
            const leftAnchor = left.lastRunAt || left.updatedAt || left.createdAt;
            const rightAnchor = right.lastRunAt || right.updatedAt || right.createdAt;
            return new Date(rightAnchor || 0).getTime() - new Date(leftAnchor || 0).getTime();
          })
          .slice(0, 10);
        setRecentSchedules(ordered);
      })
      .catch(() => {
        if (!cancelled) {
          setRecentSchedules([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token]);

  const scheduleOptions = useMemo(() => {
    if (!scheduleId || recentSchedules.some((schedule) => schedule.id === scheduleId)) {
      return recentSchedules;
    }
    return [
      {
        id: scheduleId,
        name: `Schedule ${scheduleId.slice(0, 8)}`,
      } as Schedule,
      ...recentSchedules,
    ];
  }, [recentSchedules, scheduleId]);

  const updateSearchParam = React.useCallback((key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    next.delete('session');
    next.delete('run');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearFilters = React.useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('schedule');
    next.delete('trigger');
    next.delete('dateFrom');
    next.delete('dateTo');
    next.delete('session');
    next.delete('run');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePreferredSessionHandled = React.useCallback(() => {
    if (!preferredSessionId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('session');
    next.delete('run');
    setSearchParams(next, { replace: true });
  }, [preferredSessionId, searchParams, setSearchParams]);

  if (authLoading) {
    return (
      <div className={`flex h-[100dvh] items-center justify-center overflow-hidden ${isDarkMode ? 'bg-[#0f1623]' : 'bg-gray-50'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#FFB52B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`h-[100dvh] overflow-hidden ${isDarkMode ? 'bg-[#0f1623]' : 'bg-gray-50'}`}>
        <Login />
      </div>
    );
  }

  return (
    <div className={`flex h-[100dvh] max-h-[100dvh] min-h-0 w-full min-w-0 flex-col overflow-hidden overscroll-none ${isDarkMode ? 'dark bg-[#0f1623]' : 'bg-gray-50'}`}>
      <div className="shrink-0 overflow-hidden border-b border-gray-200/70 bg-white/80 backdrop-blur dark:border-white/[0.08] dark:bg-[#121826]/85">
        <div className="mx-auto flex max-w-[1800px] min-w-0 flex-col gap-4 px-4 py-3 sm:px-6">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200/70 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/[0.08] dark:text-gray-300 dark:hover:bg-white/[0.06]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <Bot className="h-4 w-4 text-[#FFB52B]" />
                Agents
              </div>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                Global agent sessions explorer
              </p>
            </div>
            <button
              type="button"
              onClick={toggleDarkMode}
              className="ml-auto inline-flex items-center justify-center rounded-lg border border-gray-200/70 p-2 text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/[0.08] dark:text-gray-300 dark:hover:bg-white/[0.06]"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
          <div className="flex min-w-0 flex-wrap items-end gap-2">
            <label className="flex min-w-[200px] flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Schedule ID
              <input
                type="text"
                value={scheduleId || ''}
                onChange={(event) => updateSearchParam('schedule', event.target.value)}
                placeholder="paste schedule id"
                list="recent-agent-schedules"
                className="rounded-lg border border-gray-200/70 bg-white px-3 py-2 text-xs font-normal normal-case text-gray-700 outline-none transition-colors focus:border-[#FFB52B] dark:border-white/[0.08] dark:bg-[#0f1623] dark:text-gray-200"
              />
            </label>
            <datalist id="recent-agent-schedules">
              {scheduleOptions.map((schedule) => (
                <option key={schedule.id} value={schedule.id}>
                  {schedule.name}
                </option>
              ))}
            </datalist>
            <label className="flex min-w-[220px] flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Recent Schedule
              <select
                value={scheduleOptions.some((schedule) => schedule.id === (scheduleId || '')) ? (scheduleId || '') : ''}
                onChange={(event) => updateSearchParam('schedule', event.target.value)}
                className="rounded-lg border border-gray-200/70 bg-white px-3 py-2 text-xs font-normal normal-case text-gray-700 outline-none transition-colors focus:border-[#FFB52B] dark:border-white/[0.08] dark:bg-[#0f1623] dark:text-gray-200"
              >
                <option value="">Recent schedules</option>
                {scheduleOptions.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name} · {schedule.id.slice(0, 8)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex min-w-[130px] flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Trigger
              <select
                value={triggerType || ''}
                onChange={(event) => updateSearchParam('trigger', event.target.value)}
                className="rounded-lg border border-gray-200/70 bg-white px-3 py-2 text-xs font-normal normal-case text-gray-700 outline-none transition-colors focus:border-[#FFB52B] dark:border-white/[0.08] dark:bg-[#0f1623] dark:text-gray-200"
              >
                <option value="">All triggers</option>
                <option value="manual">Manual</option>
                <option value="cron">Cron</option>
                <option value="event">Event</option>
                <option value="dependency">Dependency</option>
              </select>
            </label>
            <label className="flex min-w-[150px] flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              From
              <input
                type="date"
                value={dateFrom || ''}
                onChange={(event) => updateSearchParam('dateFrom', event.target.value)}
                className="rounded-lg border border-gray-200/70 bg-white px-3 py-2 text-xs font-normal normal-case text-gray-700 outline-none transition-colors focus:border-[#FFB52B] dark:border-white/[0.08] dark:bg-[#0f1623] dark:text-gray-200"
              />
            </label>
            <label className="flex min-w-[150px] flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              To
              <input
                type="date"
                value={dateTo || ''}
                onChange={(event) => updateSearchParam('dateTo', event.target.value)}
                className="rounded-lg border border-gray-200/70 bg-white px-3 py-2 text-xs font-normal normal-case text-gray-700 outline-none transition-colors focus:border-[#FFB52B] dark:border-white/[0.08] dark:bg-[#0f1623] dark:text-gray-200"
              />
            </label>
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-lg border border-gray-200/70 px-3 py-2 text-xs text-gray-600 transition-colors hover:bg-gray-100 dark:border-white/[0.08] dark:text-gray-300 dark:hover:bg-white/[0.06]"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
        <AgentChat
          token={token || undefined}
          preferredSessionId={preferredSessionId}
          onPreferredSessionHandled={handlePreferredSessionHandled}
          sessionListOptions={sessionListOptions}
          sessionListLabel={sessionListLabel}
        />
      </div>
    </div>
  );
};

export default AgentsPage;
